import React, { useState, useEffect, useCallback } from "react";
import socket from "src/socket";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaComments } from "react-icons/fa";
import Navbar from "../common/Navbar";
import OneToOneChat from "./OneToOneChat";
import messageApi from "../../api/message";

const ConversationsList = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const navigate = useNavigate();
  const isLargeScreen = window.innerWidth >= 1024;

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const name = localStorage.getItem("fullName");
    if (userId) {
      setCurrentUser({ _id: userId, name });
      socket.emit("join", userId);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!currentUser?._id) return;
    try {
      const res = await messageApi.getConversations(currentUser._id);
      if (Array.isArray(res.data)) {
        setConversations(res.data);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?._id]);

  useEffect(() => {
    fetchConversations();
    socket.on("receiveMessage", fetchConversations);
    return () => socket.off("receiveMessage");
  }, [fetchConversations]);

  useEffect(() => {
    let filtered = [...conversations];
    if (searchQuery) {
      filtered = filtered.filter((c) =>
        c.userName?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (activeFilter === "unread") {
      filtered = filtered.filter((c) => c.unread > 0);
    }
    setFilteredConversations(filtered);
  }, [conversations, searchQuery, activeFilter]);

  const handleSelect = (conv) => {
    // 🔥 Sabse important fix: Hum objects ko correctly wrap karke bhej rahe hain
    const other = {
      _id: conv.userId,
      name: conv.userName,
      avatar: conv.userAvatar,
    };

    if (isLargeScreen) {
      setSelectedUser(other);
      setConversations((prev) =>
        prev.map((c) => (c.userId === conv.userId ? { ...c, unread: 0 } : c)),
      );
    } else {
      navigate(`/chat/${conv.userId}`, { state: other });
    }
  };

  if (isLoading)
    return <div className="p-10 text-center font-bold">Loading...</div>;

  return (
    <div className="flex flex-col h-screen bg-[#f5f5f5]">
      <Navbar />
      <div className="flex flex-1 overflow-hidden mt-16 sm:mt-20 p-2 sm:p-4">
        {/* SIDEBAR */}
        <div
          className={`w-full lg:w-[300px] xl:w-[320px] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden ${selectedUser && !isLargeScreen ? "hidden" : "flex"}`}
        >
          <div className="p-4 border-b border-gray-100 bg-white">
            <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
            <div className="mt-3 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                placeholder="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 rounded-xl bg-gray-100 border border-transparent focus:border-[#FF4D12]/30 focus:bg-white focus:outline-none px-9 text-sm text-gray-700 placeholder:text-gray-400"
              />
            </div>
          </div>

          <div className="flex p-3 gap-2 border-b border-gray-100 bg-white">
            <button
              onClick={() => setActiveFilter("all")}
              className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all ${activeFilter === "all" ? "bg-[#FF4D12] text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              All
            </button>
            <button
              onClick={() => setActiveFilter("unread")}
              className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all ${activeFilter === "unread" ? "bg-[#FF4D12] text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              Unread
            </button>
          </div>

          <div className="flex-1 overflow-y-auto bg-[#fafafa] p-2">
            {filteredConversations.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">
                No conversations yet
              </div>
            ) : (
              filteredConversations.map((c) => (
                <div
                  key={c.userId}
                  onClick={() => handleSelect(c)}
                  className={`p-3 rounded-xl cursor-pointer flex items-center gap-3 transition-all duration-200 mb-1 border ${selectedUser?._id === c.userId ? "bg-[#FFF5F0] border-orange-100 shadow-sm" : "bg-white border-transparent hover:bg-[#FFF5F0] hover:border-orange-50"}`}
                >
                  <div className="relative">
                    {c.userAvatar ? (
                      <img
                        src={c.userAvatar}
                        className="w-12 h-12 rounded-full object-cover border border-orange-100"
                        alt=""
                      />
                    ) : (
                      <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center font-semibold text-[#FF4D12] border border-orange-100">
                        {c.userName ? c.userName.charAt(0).toUpperCase() : "U"}
                      </div>
                    )}
                    {c.unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#FF4D12] text-white text-[10px] min-w-5 h-5 px-1 rounded-full flex items-center justify-center font-bold border-2 border-white">
                        {c.unread > 99 ? "99+" : c.unread}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center gap-2">
                      <h4 className="font-semibold text-gray-900 truncate text-sm">
                        {c.userName || "User"}
                      </h4>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {new Date(c.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {c.lastMessage}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* CHAT AREA */}
        <div className="flex-1 flex flex-col bg-transparent lg:pl-4">
          {selectedUser ? (
            <div className="h-full rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <OneToOneChat
                currentUser={currentUser}
                otherUser={selectedUser}
                key={selectedUser._id}
              />
            </div>
          ) : (
            <div className="hidden lg:flex flex-col items-center justify-center h-full rounded-2xl border border-gray-200 bg-white text-gray-300 shadow-sm">
              <FaComments size={50} className="mb-4 opacity-20" />
              <p className="text-lg">Select a conversation to chat</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationsList;
